import type { Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import type { Permission } from '@/types/permissions';

export interface RouteDefinition<TParams = never> {
  name: string;
  path: string;
  component?: () => Promise<Component>;
  redirect?: string | ((to: Record<string, unknown>) => string | { name: string; params?: Record<string, unknown> });
  params?: TParams;
  meta?: {
    requiresAuth?: boolean;
    requiresGuest?: boolean;
    requiresEmployee?: boolean;
    requiresCustomer?: boolean;
    requiresCompany?: boolean;
    requiresOwner?: boolean;
    permission?: Permission;
    context?: 'employee' | 'customer';
    hideNavbar?: boolean;
  };
  menus?: {
    main?: {
      label: string;
      icon?: string;
      order?: number;
      show?: boolean;
      group?: string;
    };
    user?: {
      label: string;
      icon?: string;
      order?: number;
      show?: boolean;
      group?: string;
    };
    footer?: {
      label: string;
      icon?: string;
      order?: number;
      show?: boolean;
      group?: string;
    };
  };
  children?: Record<string, RouteDefinition<unknown>>;
}

export { routeDefinitions } from './route-definitions';

export function generateRouteRecords(
  definitions: Record<string, RouteDefinition<unknown> | Record<string, unknown>>,
): RouteRecordRaw[] {
  const records: RouteRecordRaw[] = [];

  function processDefinition(def: RouteDefinition<unknown> | Record<string, unknown>): RouteRecordRaw | null {
    if ('name' in def && 'path' in def) {
      const routeDef = def as RouteDefinition<unknown>;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const record: any = {
        name: routeDef.name,
        path: routeDef.path,
        meta: routeDef.meta,
      };

      if (routeDef.redirect) {
        record.redirect = routeDef.redirect;
      }

      if (routeDef.component) {
        record.component = routeDef.component;
      }

      if (routeDef.children) {
        record.children = [];
        Object.values(routeDef.children).forEach(childDef => {
          const childRecord = processDefinition(childDef);
          if (childRecord) {
            record.children.push(childRecord);
          }
        });
      }

      return record as RouteRecordRaw;
    }

    return null;
  }

  function processObject(obj: Record<string, unknown>): void {
    Object.values(obj).forEach(value => {
      if (typeof value === 'object' && value !== null && !('$$typeof' in value)) {
        if ('name' in value && 'path' in value) {
          const record = processDefinition(value as RouteDefinition<unknown>);
          if (record) {
            records.push(record);
          }
        } else {
          processObject(value as Record<string, unknown>);
        }
      }
    });
  }

  processObject(definitions);

  return records;
}

export interface MenuItem {
  label: string;
  icon?: string;
  route?: { name: string; path: string };
  order: number;
  permission?: string;
  location: 'main' | 'user' | 'footer';
  group?: string;
  items?: MenuItem[];
}

export function generateMenuItems(
  definitions: Record<string, RouteDefinition<unknown> | Record<string, unknown>>,
  context?: 'employee' | 'customer',
  location?: 'main' | 'user' | 'footer',
): MenuItem[] {
  const items: MenuItem[] = [];
  const targetLocation = location ?? 'main';

  function processDefinition(def: RouteDefinition<unknown> | Record<string, unknown>): void {
    if ('name' in def && 'path' in def && 'menus' in def) {
      const routeDef = def as RouteDefinition<unknown>;

      if (!context || routeDef.meta?.context === context) {
        const menuConfig = routeDef.menus?.[targetLocation];

        if (menuConfig) {
          items.push({
            label: menuConfig.label,
            icon: menuConfig.icon,
            route: { name: routeDef.name, path: routeDef.path },
            order: menuConfig.order ?? 999,
            permission: routeDef.meta?.permission,
            location: targetLocation,
            group: menuConfig.group,
          });
        }
      }
    }

    if ('children' in def && def.children) {
      Object.values(def.children).forEach(processDefinition);
    } else {
      Object.values(def).forEach(value => {
        if (typeof value === 'object' && value !== null && !('$$typeof' in value)) {
          processDefinition(value);
        }
      });
    }
  }

  Object.values(definitions).forEach(processDefinition);

  const sortedItems = items.toSorted((a: MenuItem, b: MenuItem) => a.order - b.order);

  const groupedItems: MenuItem[] = [];
  const groups = new Map<string, MenuItem[]>();

  sortedItems.forEach((item: MenuItem) => {
    if (item.group) {
      if (!groups.has(item.group)) {
        groups.set(item.group, []);
      }
      groups.get(item.group)!.push(item);
    } else {
      groupedItems.push(item);
    }
  });

  groups.forEach((groupItems, groupName) => {
    const groupOrder = groupItems[0]?.order ?? 999;

    groupedItems.push({
      label: groupName,
      icon: groupItems[0]?.icon,
      order: groupOrder,
      location: targetLocation,
      items: groupItems,
    });
  });

  return groupedItems.toSorted((a: MenuItem, b: MenuItem) => a.order - b.order);
}
